import React, { useCallback, useMemo } from 'react';
import { Flex, Box, Text, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash-es';
import useDollarsTextFromCents from '../../../../../../../hooks/useDollarsTextFromCents';
import {
  MEMBER_TIER_EFFECT_TYPE_SINGLE_PURCHASE,
  MEMBER_TIER_EFFECT_TYPE_WITHIN_INTERVAL,
} from '../../../constants/memberTiers';
import { useAppContext } from '../../../../../context/appContext';
import { useProfileContext } from '../../../context/profileContext';
import { memberTierRuleType } from '../../../propTypes';
import ProgressBar from '../components/ProgressBar';
import { getProgressPercentage } from '../utils';

const MemberUpgradeTier = ({ upgradeRule }) => {
  const { slFeatureService } = useAppContext();
  const { userData } = useProfileContext();
  const { t } = useTranslation();
  const getDollarsTextFromCents = useDollarsTextFromCents();
  const membershipTierGap = userData.membership_tier_gap;

  const getUpgradeCondition = useCallback(
    (rule, type) => {
      if (type === MEMBER_TIER_EFFECT_TYPE_SINGLE_PURCHASE) {
        return t('users.membership.tier.upgrade.to_next_level.single');
      } else {
        return t('users.membership.tier.upgrade.to_next_level.within', {
          month: rule.condition_interval.time_value,
        });
      }
    },
    [t],
  );

  const upgradeTierInfo = useMemo(() => {
    if (isEmpty(membershipTierGap.next_tier)) {
      return {};
    }

    const upgradeType = upgradeRule?.condition_interval.type;
    const userSpending = membershipTierGap.user_spending ?? 0;
    const nextTotalSpending = membershipTierGap.next_total_spending ?? 0;

    return {
      nextSpending:
        upgradeType === MEMBER_TIER_EFFECT_TYPE_SINGLE_PURCHASE
          ? getDollarsTextFromCents(upgradeRule.total_spending.cents ?? 0)
          : undefined,
      upgradeType,
      progressPercentage: getProgressPercentage(membershipTierGap),
      currentSpending: getDollarsTextFromCents(userSpending),
      gapSpending: getDollarsTextFromCents(nextTotalSpending - userSpending),
      upgradeCondition: getUpgradeCondition(upgradeRule, upgradeType),
    };
  }, [
    getDollarsTextFromCents,
    getUpgradeCondition,
    membershipTierGap,
    upgradeRule,
  ]);

  return isEmpty(membershipTierGap.next_tier) ? null : (
    <Box textAlign="left">
      <Flex
        justifyContent="flex-start"
        marginBottom="10px"
        className="member-center-profile-section_title"
      >
        <Box fontSize="15px" color="#dddddd" marginRight="10px">
          <i
            className="fa fa-flag member-center-profile__section-title-icon"
            aria-hidden="true"
          ></i>
        </Box>
        <Text
          flex="1"
          variant="sectionTitle"
          className="member-center-profile__section-title-text"
        >
          {upgradeTierInfo.upgradeCondition}
        </Text>
      </Flex>
      {upgradeTierInfo.upgradeType ===
        MEMBER_TIER_EFFECT_TYPE_WITHIN_INTERVAL && (
        <>
          <Flex justifyContent="space-between">
            <Text
              variant="description"
              className="member-center-profile__description-text member-center-profile__upgrade-tier-text"
            >
              {upgradeTierInfo.currentSpending}
            </Text>
            <Text variant="hint" className="member-center-profile__hint-text">
              {upgradeTierInfo.progressPercentage === 100
                ? t('users.membership.tier.upgrade.completed', {
                    type: 'within_interval',
                  })
                : t('users.membership.tier.upgrade.gap_dollar', {
                    dollar: upgradeTierInfo.gapSpending,
                  })}
            </Text>
          </Flex>
          <ProgressBar percentage={upgradeTierInfo.progressPercentage} />
        </>
      )}
      {upgradeTierInfo.upgradeType ===
        MEMBER_TIER_EFFECT_TYPE_SINGLE_PURCHASE &&
        (upgradeTierInfo.progressPercentage === 100 ? (
          <Text
            variant="description"
            className="member-center-profile__description-text member-center-profile__upgrade-tier-text"
          >
            {t('users.membership.tier.upgrade.completed', {
              type: 'single_purchase',
            })}
          </Text>
        ) : (
          <div>
            <Text
              variant="description"
              className="member-center-profile__description-text member-center-profile__upgrade-tier-text"
              display="inline"
              verticalAlign="middle"
            >
              {t('users.membership.tier.upgrade.condition.dollar', {
                dollar: upgradeTierInfo.nextSpending,
              })}
            </Text>
            <Button
              display="inline"
              variant="textButton"
              className="member-center-profile__text-button"
              onClick={() => window.open('/')}
            >
              {t('users.membership.tier.upgrade.condition.continue')}
            </Button>
          </div>
        ))}
      <Text
        variant="hint"
        className="member-center-profile__hint-text member-center-profile__upgrade-tier-description"
        marginTop="10px"
        fontSize="12px"
      >
        {slFeatureService.hasFeature('tier_auto_downgrade')
          ? t('users.membership.tier.expire.date.auto_downgrade.description')
          : t('users.membership.tier.expire.date.description')}
      </Text>
    </Box>
  );
};

MemberUpgradeTier.propTypes = {
  upgradeRule: memberTierRuleType,
};

export default MemberUpgradeTier;
